import React from "react";
import {
  Box,
  Text,
  Badge,
  Image,
  VStack,
  HStack,
  Flex,
  SimpleGrid,
  Button,
} from "@chakra-ui/react";

const ApiQuickStart = () => {
  // Checkmark Icon Component
  const CheckmarkIcon = () => (
    <Box
      w="16px"
      h="16px"
      borderRadius="50%"
      bg="#10B981"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.33329 2.5L3.74996 7.08333L1.66663 5"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );

  const steps = [
    {
      number: 1,
      icon: "assets/account-plus.svg",
      title: "Create a Business Account",
      description:
        "Sign up and verify your business details. Quick and simple registration process with instant account creation.",
      bullets: [
        "Business information",
        "Director details",
        "Document upload",
      ],
    },
    {
      number: 2,
      icon: "assets/key-24.svg",
      title: "Request API Access",
      description:
        "Access sandbox and production credentials via the BizFlex Developer Portal. Start testing immediately.",
      bullets: [
        "Sandbox credentials",
        "API documentation",
        "Code samples",
      ],
    },
    {
      number: 3,
      icon: "assets/account.svg",
      title: "Integrate Endpoints",
      description:
        "Use our detailed API documentation to connect and test. Developer-friendly with extensive examples.",
      bullets: [
        "RESTful APIs",
        "Webhooks support",
        "Test environment",
      ],
    },
    {
      number: 4,
      icon: "assets/acount.svg",
      title: "Go Live",
      description:
        "Move to production after KYC and compliance review. Start processing real transactions within days.",
      bullets: [
        "KYC verification",
        "Compliance review",
        "Production access",
      ],
    },
  ];

  return (
    <Box
      bg="#ffffff"
      w="100%"
      py={{ base: "60px", md: "100px" }}
      px={{ base: "20px", md: "80px", lg: "120px" }}
    >
      <VStack spacing="60px" align="center" w="100%">
        {/* Top Section */}
        <VStack spacing="24px" align="center" w="100%" maxW="1200px">
          <Badge
            display="flex"
            alignItems="center"
            gap="8px"
            w="fit-content"
            bg="#B0C9FF"
            color="#2463EB"
            px="16px"
            py="8px"
            border="1px solid #2463EB"
            borderRadius="20px"
            fontSize="12px"
            fontWeight={400}
          >
            <Image
              src="assets/rocket-fill.svg"
              alt="quick-start"
              w="16px"
              h="16px"
            />
            Quick Start
          </Badge>

          <Text
            fontSize={{ base: "32px", md: "48px" }}
            fontWeight={600}
            textAlign="center"
            color="#0F172B"
            lineHeight={{ base: "38.4px", md: "57.6px" }}
          >
            Comprehensive Product Suite
          </Text>

          <Text
            fontSize={{ base: "14px", md: "18px" }}
            textAlign="center"
            color="#64748B"
            maxW="700px"
            lineHeight={{ base: "21px", md: "27px" }}
          >
            Explore BizFlex's modular APIs built to power financial innovation.
          </Text>
        </VStack>

        {/* Four-Step Process Cards */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 4 }}
          spacing={{ base: "24px", md: "32px" }}
          w="100%"
          maxW="1400px"
        >
          {steps.map((step, index) => (
            <Box
              key={step.number}
              bg="#F4FAFF"
              borderRadius="16px"
              py="32px"
              px="16px"
              border={step.number === 4 ? "1px solid #5C5C5C66" : "1px solid #89CFF4"}
              position="relative"
              h="100%"
            >
              {/* Number Circle - green for step 4, blue for 1-3 */}
              <Box
                position="absolute"
                top="-20px"
                left="-20px"
                w="30px"
                h="30px"
                borderRadius="50%"
                bg={step.number === 4 ? "#059669" : "#0284C7"}
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
              >
                <Text fontSize="16px" fontWeight={500} color="#FFFFFF">
                  {step.number}
                </Text>
              </Box>

              <VStack align="flex-start" spacing="20px" w="100%" h="100%" pt="8px">
                {/* Icon */}
                <Box
                  w="48px"
                  h="48px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Image
                    src={step.icon}
                    alt={step.title}
                    w="48px"
                    h="48px"
                  />
                </Box>

                {/* Title */}
                <Text
                  fontSize={{ base: "14px", md: "18px" }}
                  fontWeight={600}
                  color="#0F172B"
                >
                  {step.title}
                </Text>

                {/* Description */}
                <Text
                  fontSize={{ base: "12px", md: "12px" }}
                  color="#64748B"
                  lineHeight="16px"
                >
                  {step.description}
                </Text>

                {/* Bullet Points with Checkmark Icons */}
                <VStack align="flex-start" spacing="12px" w="100%" flex={1}>
                  {step.bullets.map((bullet, bulletIndex) => (
                    <HStack
                      key={bulletIndex}
                      spacing="12px"
                      align="flex-start"
                      w="100%"
                    >
                      <CheckmarkIcon />
                      <Text
                        fontSize={{ base: "13px", md: "14px" }}
                        color="#475569"
                        lineHeight="20px"
                      >
                        {bullet}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>

        {/* Call to Action Section */}
        <VStack
          spacing="24px"
          align="center"
          w="100%"
          bg="#F2F7FB"
          maxW="1400px"
          borderRadius="16px"
          px={{ base: "16px", md: "24px" }}
          pt={{ base: "24px", md: "40px" }}
          pb={{ base: "24px", md: "40px" }}
          boxShadow="0 1px 3px rgba(0, 0, 0, 0.06)"
        >
          <Text
            fontSize={{ base: "28px", md: "36px" }}
            fontWeight={700}
            textAlign="center"
            color="#2E2E2E"
            lineHeight={{ base: "33.6px", md: "43.2px" }}
          >
            Ready to get started?
          </Text>

          <Text
            fontSize={{ base: "14px", md: "18px" }}
            textAlign="center"
            color="#5C5C5C"
            lineHeight={{ base: "21px", md: "27px" }}
            maxW="560px"
          >
            Join hundreds of businesses already using BizFlex to power their
            financial operations. Start building today.
          </Text>

          <Button
            bg="#2463EB"
            borderRadius="12px"
            h="56px"
            mb="0"
            w={{ base: "100%", md: "320px" }}
            px="24px"
            color="#fff"
            fontWeight={600}
            fontSize="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="10px"
            _hover={{ bg: "#1a4fc7" }}
          >
            Create a Business Account
            <Image
              src="assets/arrow-right.svg"
              alt=""
              w="20px"
              h="20px"
            />
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
};

export default ApiQuickStart;

